import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import GenericButton from '../../../components/button';
import {TouchableOpacity, View} from 'react-native';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import Animated, {FadeInDown} from 'react-native-reanimated';
import {tr} from '../../../translations';
import {endSession} from '../login/actions';
import MainLayout from '../../mainLayout';
import DatePicker from '../../../components/datePicker';
import Icon from '../../../components/icon';
import TextInput from '../../../components/textInput';
import {hdp} from '../../../utils/responsive';
import Picker from '../../../components/picker';
import TextGeneric from '../../../components/textGeneric';
import {handleChange} from '../../../utils/form';
import {
  convertToYYYYMMDD,
  getSettingByKey,
  getUserDetailsByIds,
  isValidDate,
  openUrl,
} from '../../../utils/helpers';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {updateUser} from '../../content/profile/actions';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import {AnalyticsEvents, modalTypes, SettingKey} from '../../../utils/enums';
import {analyticsService} from '../../../utils/services/analytics';
import {Theme} from '../../../theme/lightTheme';
import {Space} from '../../content/home';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';
import {getSpecialities} from '../../content/home/actions';
import {SpecialityType} from '../../../interfaces/api/auth/login';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';

const ContentContainer = styled.View<{wdp: any; hdp: any}>`
  padding: ${({hdp}) => hdp(5)}px ${({wdp}) => wdp(5)}px;
`;

const LogoContainer = styled(Animated.View)`
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const Slogan = styled(TextGeneric)<{paddingBottom: number}>`
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const SubmitContainer = styled.View`
  margin-top: 20px;
  align-self: center;
`;

const Row = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: flex-start;
`;

const FormItemContainer = styled.View<{isLargeScreen: boolean}>`
  width: ${({isLargeScreen}) => (isLargeScreen ? '48%' : '100%')};
`;

const CheckView = styled(TouchableOpacity)`
  width: ${({theme}) => (theme as Theme).text.s55}px;
  aspect-ratio: 1;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${({theme}) => (theme as Theme).secondary};
  justify-content: center;
  align-items: center;
  margin-right: ${({theme}) => (theme as Theme).text.s55}px;
`;

const TermsLabelView = styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
`;

const LabelText = styled(TextGeneric)`
  font-size: ${({theme}) => (theme as Theme).text.s7}px;
  color: ${({theme}) => (theme as Theme).text.dark};
`;

const LabelTextLink = styled(TextGeneric)`
  font-size: ${({theme}) => (theme as Theme).text.s8}px;
  color: ${({theme}) => (theme as Theme).text.dark};
  text-decoration: ${({theme}) => (theme as Theme).text.dark} underline;
`;

const CheckViewContainer = styled.View`
  flex-direction: row;
  align-items: center;
  width: 92%;
  margin-top: ${hdp(4)}px;
`;

const SubmitButton = styled(GenericButton)`
  margin-top: ${hdp(4)}px;
  width: 90%;
  border-radius: 100px;
  align-self: center;
  background-color: ${({disabled, theme}) =>
    disabled ? (theme as Theme).text.light : (theme as Theme).primary};
`;

// Define the mandatory fields for the form
const mandatoryFields = [
  'firstName',
  'lastName',
  'bod',
  'gender',
  'country',
  'specialty',
];

const ANIMATION_DURATION = 400;

const AfterLoginPage = ({navigation}: ScreenProps) => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {
    isLargeScreen,
    hdp: hdpHook,
    wdp: wdpHook,
    getByScreenSize,
  } = useResponsiveDimensions();
  const {currentUser, config} = useSelector(
    (state: RootState) => state.auth,
  ) || {
    currentUser: {},
  };
  const [agree, setChecked] = useState(false);
  const [canGoBack, setGoBack] = useState(false);
  const [account, setAccount] = useState<any[]>([]);
  const [localSpecialties, setLocalSpecialties] = useState<SpecialityType[]>(
    config?.specialties || [],
  );
  const entryTime = useRef(new Date());

  const [userDetails, setUserDetails] = useState<any>({
    gender: undefined,
    country: undefined,
    profession: undefined,
    specialty: undefined,
    language: undefined,
  });

  useEffect(() => {
    return () => {
      const timeSpent = entryTime
        ? (new Date().getTime() - entryTime.current.getTime()) / 1000
        : 0;

      analyticsService?.logEvent(AnalyticsEvents.OpenAfterLogin, {
        timeSpent,
      });
    };
  }, []);

  useEffect(() => {
    const {gender_id, profession_id, country_id, language_id, specialty_id} =
    currentUser || {};
    const details = getUserDetailsByIds({
      config,
      country_id,
      gender_id,
      profession_id,
      language_id,
      specialty_id,
    });
    setUserDetails(details);
  }, [currentUser, config]);

  const [form, updateForm] = useState<any>({});
  const {first_name, last_name, date_of_birth} = currentUser || {};
  useEffect(() => {
    if (currentUser && config) {
      const {gender, country, profession, specialty, language} = userDetails;
      const tempItem: any = {
        firstName: first_name || '',
        lastName: last_name || '',
        gender: gender || '',
        country: country || '',
        profession: profession || '',
        language: language || '',
        specialty: specialty || '',
        bod: isValidDate(date_of_birth) ? date_of_birth : '',
      };
      const tempAccount = mandatoryFields.map(label => {
        return {label, value: tempItem[label] || ''};
      });
      updateForm(tempItem);
      setAccount(tempAccount);
    }
  }, [currentUser, config, userDetails]);

  useEffect(
    () =>
      navigation?.addListener('beforeRemove', (e: any) => {
        if (!canGoBack) {
          e.preventDefault();
        }
      }),
    [navigation, canGoBack],
  );

  useEffect(() => {
    if (canGoBack) {
      navigation?.goBack();
    }
  }, [canGoBack]);

  const updateSpecialities = async () => {
    const res = await getSpecialities(form?.profession?.id);
    setLocalSpecialties(res);
    handleChange('specialty', res?.[0] || null, updateForm);
  };

  useEffect(() => {
    updateSpecialities();
  }, [form.profession]);

  const logout = () => {
    return openQuestionModal({
      title: tr('afterLogin.logoutTitle'),
      message: tr('afterLogin.logoutMessage'),
      onConfirm: () => endSession(),
      type: modalTypes.question,
    });
  };

  const submit = async () => {
    const {
      id,
      specialty_id,
      country_id,
      date_of_birth,
      first_name,
      gender,
      last_name,
      language_id,
      profession_id,
    } = currentUser || {};
    const body: any = {
      id,
      specialty_id: form?.specialty?.id || specialty_id || 1,
      country_id: form?.country?.id || country_id || 1,
      date_of_birth: convertToYYYYMMDD(form?.bod) ?? date_of_birth,
      first_name: form?.firstName || first_name,
      last_name: form?.lastName || last_name,
      gender_id: form?.gender?.id || gender,
      language_id,
      profession_id: profession_id || 1,
      is_first_login: 0,
    };

    const res = await updateUser(body);
    if (res) {
      setGoBack(true);
    }
  };

  const _getPickerList = (label: string): any[] => {
    switch (label) {
      case 'gender':
        return config?.genders || [];
      case 'language':
        return config?.languages || [];
      case 'profession':
        return config?.professions || [];
      case 'specialty':
        return localSpecialties || [];
      case 'country':
        return config?.countries || [];
      default:
        return [];
    }
  };

  const getObjectByName = (label: string, name: string | number): any => {
    const data = _getPickerList(label);
    const idx = data.findIndex(i => i.name === name);
    if (idx > -1) {
      return data[idx];
    }
    return {};
  };

  const renderFormItem = (item: string) => {
    if (item === 'bod') {
      return (
        <DatePicker
          key={item}
          label={tr(`register.${item}`)}
          value={convertToYYYYMMDD(form[item]) ?? ''}
          maxDate={new Date().toISOString()}
          style={{
            marginBottom: hdpHook(2),
          }}
          onValueChange={text => handleChange(item, text, updateForm)}
          format={'YYYY-MM-DD'}
        />
      );
    }
    if (
      item === 'gender' ||
      item === 'language' ||
      item === 'profession' ||
      item === 'specialty' ||
      item === 'country'
    ) {
      return (
        <Picker
          key={item}
          label={tr(`register.${item}`)}
          containerStyle={{
            marginBottom: hdpHook(2),
          }}
          list={_getPickerList(item).map(i => i?.name || '')}
          value={form[item] ? form[item].name : ''}
          onValueChange={val => {
            const value = getObjectByName(item, val);
            handleChange(item, value, updateForm);
          }}
        />
      );
    }
    return (
      <TextInput
        key={item}
        label={tr(`register.${item}`)}
        value={form[item]}
        onChangeText={text => handleChange(item, text, updateForm)}
        containerStyle={{
          marginBottom: hdpHook(2),
        }}
      />
    );
  };

  return (
    <MainLayout
      backHeader
      onGoBackPress={logout}
      title={tr('afterLogin.title')}
      showOverlay={false}
      noPadding
      keyboardAvoidScrollView
      noPaddingHeader>
      <ContentContainer wdp={wdpHook} hdp={hdpHook}>
        <LogoContainer entering={FadeInDown.duration(ANIMATION_DURATION)}>
          <Icon
            name={'genio'}
            width={wdpHook(27.3)}
            height={hdpHook(7.3)}
            color={theme.secondary}
          />
          <Slogan bold color={theme.text.dark} paddingBottom={hdpHook(5)}>
            {tr('app.slogan')}
          </Slogan>
        </LogoContainer>

        <Animated.View
          entering={FadeInDown.duration(ANIMATION_DURATION).delay(
            ANIMATION_DURATION,
          )}>
          {isLargeScreen
            ? mandatoryFields.reduce((acc, field, index) => {
              if (index % 2 === 0) {
                const nextField = mandatoryFields[index + 1];
                acc.push(
                  <Row key={field}>
                    <FormItemContainer isLargeScreen={isLargeScreen}>
                      {renderFormItem(field)}
                    </FormItemContainer>
                    {nextField && (
                      <FormItemContainer isLargeScreen={isLargeScreen}>
                        {renderFormItem(nextField)}
                      </FormItemContainer>
                    )}
                  </Row>,
                );
              }
              return acc;
            }, [] as React.ReactElement[])
            : mandatoryFields.map(item => (
              <FormItemContainer key={item} isLargeScreen={isLargeScreen}>
                {renderFormItem(item)}
              </FormItemContainer>
            ))}

          <CheckViewContainer>
            <CheckView
              onPress={() => setChecked(!agree)}
              style={{
                backgroundColor: agree ? theme.secondary : 'transparent',
              }}>
              {agree && (
                <Icon
                  name={'checkSelect'}
                  color={theme.text.light}
                  size={theme.text.s6}
                />
              )}
            </CheckView>

            <TermsLabelView>
              <TouchableOpacity onPress={() => setChecked(!agree)}>
                <LabelText>{tr('afterLogin.termsPart1')}</LabelText>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() =>
                  openUrl(getSettingByKey(SettingKey.TermsConditions))
                }>
                <LabelTextLink bold>
                  {tr('afterLogin.termsPart2')}
                </LabelTextLink>
              </TouchableOpacity>
              <LabelText>{tr('afterLogin.termsPart3')}</LabelText>
              <TouchableOpacity
                onPress={() =>
                  openUrl(getSettingByKey(SettingKey.PrivacyPolicy))
                }>
                <LabelTextLink bold>
                  {tr('afterLogin.termsPart4')}
                </LabelTextLink>
              </TouchableOpacity>
            </TermsLabelView>
          </CheckViewContainer>

          <SubmitContainer style={{width: getByScreenSize('100%', '40%')}}>
            <SubmitButton
              label={tr('register.activate')}
              radius={100}
              onPress={submit}
              disabled={!agree}
              backgroundColor={!agree ? theme.text.light : theme.primary}
              labelStyle={{
                color: !agree ? theme.secondary : theme.text.light,
              }}
            />
          </SubmitContainer>
        </Animated.View>
      </ContentContainer>
      <Space bottomInset={bottomInset} extraSpace={hdpHook(2)} />
    </MainLayout>
  );
};

export default AfterLoginPage;
